import { Box, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Config360 } from "./View360";
import { CuratedConfig } from "./ViewCurated";
import { Button } from "./Button";
import { finalizeSharePanelStyleProps } from "../theme/main";
import { useFinalizeShareStore } from "../store/FinalizeShareStore";

export type FinalizeImage = {
  id: string;
  label: string;
  type: "exterior" | "interior";
  src: string;
  rotation: number;
  offsetX: number;
  offsetY: number;
};

type FinalizeSharePanelProps = {
  exteriorConfig: Config360;
  interiorConfig: CuratedConfig;
  modelName?: string;
};

type RenderConfig = {
  basePath?: string;
  options: Record<string, Record<string, string>>;
  selectedOptions?: Record<string, string>;
};

const downloadTargets = [
  { id: "exterior-06", label: "Exterior frame 06", type: "exterior", frame: 6 },
  { id: "exterior-09", label: "Exterior frame 09", type: "exterior", frame: 9 },
  {
    id: "exterior-20",
    label: "Exterior frame 20",
    type: "exterior",
    frame: 20,
  },
  {
    id: "exterior-33",
    label: "Exterior frame 33",
    type: "exterior",
    frame: 33,
  },
  { id: "interior-01", label: "Interior frame 01", type: "interior", frame: 0 },
] as const;

const previewTargets = downloadTargets.filter(
  (target) => target.type === "exterior",
);

const previewTransforms = [
  { rotation: -5, offsetX: -8, offsetY: -3 },
  { rotation: 4, offsetX: 7, offsetY: 3 },
  { rotation: -2, offsetX: 1, offsetY: 8 },
  { rotation: 6, offsetX: 12, offsetY: 12 },
];

function getDefaultSelections(options: RenderConfig["options"]) {
  return Object.entries(options).reduce<Record<string, string>>(
    (selections, [optionType, optionValues]) => {
      const optionNames = Object.keys(optionValues);
      const defaultOption =
        optionNames.find((optionName) => optionName === "Default") ||
        optionNames.find((optionName) => optionName === "None") ||
        optionNames[0];

      if (defaultOption) {
        selections[optionType] = defaultOption;
      }

      return selections;
    },
    {},
  );
}

function formatFrameNumber(frameIndex: number) {
  return frameIndex.toString().padStart(4, "0");
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });
}

async function composeRenderImage(config: RenderConfig, frameIndex: number) {
  const selections = {
    ...getDefaultSelections(config.options),
    ...config.selectedOptions,
  };
  const selectedPaths = Object.entries(config.options)
    .map(([optionType, optionValues]) => optionValues[selections[optionType]])
    .filter((path): path is string => Boolean(path));
  const paths = [
    ...(config.basePath ? [config.basePath] : []),
    ...selectedPaths,
  ];
  const images = (
    await Promise.allSettled(
      paths.map((path) =>
        loadImage(path.replace("{frame}", formatFrameNumber(frameIndex))),
      ),
    )
  )
    .map((result) => (result.status === "fulfilled" ? result.value : undefined))
    .filter((image): image is HTMLImageElement => Boolean(image));
  const sizingImage = images[0];

  if (!sizingImage) {
    return "";
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return "";
  }

  canvas.width = sizingImage.width;
  canvas.height = sizingImage.height;

  for (const image of images) {
    context.drawImage(image, 0, 0);
  }

  return canvas.toDataURL("image/png");
}

function getDownloadName(modelName: string | undefined, label: string) {
  return `${modelName || "configuration"}-${label}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

async function dataUrlToFile(src: string, fileName: string) {
  const response = await fetch(src);
  const blob = await response.blob();
  return new File([blob], `${fileName}.png`, { type: "image/png" });
}

async function cropImageFromRight(src: string, cropRatio: number) {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return src;
  }

  canvas.width = Math.round(image.width * (1 - cropRatio));
  canvas.height = image.height;
  context.drawImage(
    image,
    0,
    0,
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return canvas.toDataURL("image/png");
}

export const circularProgress = (
  <CircularProgress
    size={24}
    thickness={4}
    sx={{
      color: "rgb(255, 255, 255)",
    }}
  />
);

export function FinalizeSharePanel({
  exteriorConfig,
  interiorConfig,
  modelName,
}: FinalizeSharePanelProps) {
  const [images, setImages] = useState<FinalizeImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const getCachedImages = useFinalizeShareStore(
    (state) => state.getCachedImages,
  );
  const setGeneratedImages = useFinalizeShareStore(
    (state) => state.setGeneratedImages,
  );
  const {
    rootStyle,
    imageStackStyle,
    imageFrameStyle,
    imageStyle,
    buttonWrapStyle,
  } = finalizeSharePanelStyleProps;

  useEffect(() => {
    let isMounted = true;
    const cachedImages = getCachedImages(
      exteriorConfig,
      interiorConfig,
      modelName,
    );

    if (cachedImages) {
      setImages(cachedImages);
      setIsLoadingImages(false);
      return () => {
        isMounted = false;
      };
    }

    const loadPreviews = async () => {
      setIsLoadingImages(true);
      setImages([]);

      try {
        const composedImages = await Promise.all(
          downloadTargets.map(async (target) => {
            const config =
              target.type === "exterior" ? exteriorConfig : interiorConfig;
            const renderSrc = await composeRenderImage(config, target.frame);
            const src =
              target.type === "exterior"
                ? await cropImageFromRight(renderSrc, 0)
                : renderSrc;
            const previewIndex = previewTargets.findIndex(
              (previewTarget) => previewTarget.id === target.id,
            );
            const transform =
              previewIndex >= 0
                ? previewTransforms[previewIndex] || {
                    rotation: 0,
                    offsetX: 0,
                    offsetY: 0,
                  }
                : { rotation: 0, offsetX: 0, offsetY: 0 };

            return {
              ...target,
              ...transform,
              src,
            };
          }),
        );

        const loadedImages = composedImages.filter((image) => image.src);

        if (isMounted) {
          setImages(loadedImages);
          setGeneratedImages({
            exteriorConfig,
            interiorConfig,
            modelName,
            images: loadedImages,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingImages(false);
        }
      }
    };

    loadPreviews();

    return () => {
      isMounted = false;
    };
  }, [
    exteriorConfig,
    getCachedImages,
    interiorConfig,
    modelName,
    setGeneratedImages,
  ]);

  const downloadAndShare = useCallback(async () => {
    const files = await Promise.all(
      images.map((image) =>
        dataUrlToFile(image.src, getDownloadName(modelName, image.label)),
      ),
    );

    if (navigator.canShare?.({ files })) {
      await navigator.share({
        title: modelName || "Configuration",
        files,
      });
      return;
    }

    files.forEach((file) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(file);
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(link.href);
    });
  }, [images, modelName]);

  return (
    <Box
      sx={{
        ...rootStyle,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 4,
      }}
    >
      <Box
        sx={{
          ...imageStackStyle,
          position: "relative",
        }}
      >
        {isLoadingImages ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {circularProgress}
          </Box>
        ) : (
          images
            .filter((image) => image.type === "exterior")
            .map((image, index) => (
              <Box
                key={image.id}
                sx={{
                  ...imageFrameStyle,
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  overflow: "hidden",
                  transform: `translate(calc(-50% + ${image.offsetX}px), calc(-50% + ${image.offsetY}px)) rotate(${image.rotation}deg)`,
                  zIndex: index + 1,
                }}
              >
                <Box
                  component="img"
                  src={image.src}
                  alt={image.label}
                  sx={{
                    ...imageStyle,
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ))
        )}
      </Box>

      {/* <Box
        sx={{
          ...buttonWrapStyle,
          display: "flex",
          justifyContent: "center",
        }}
      > */}
      <Button
        sx={{
          opacity: isLoadingImages ? "0.5" : "1",
          pointerEvent: isLoadingImages ? "none" : "auto",
          cursor: isLoadingImages ? "auto" : "pointer",
        }}
        onClick={downloadAndShare}
      >
        Download & Share
      </Button>
      {/* </Box> */}
    </Box>
  );
}
